/**
 * reviewModeActionExecutor — `set_review_mode` : l'agent active ou désactive le
 * suivi des modifications, comme le bouton de la barre du canvas (#1838).
 *
 * Le verbe ne touche pas à l'éditeur lui-même : il règle le régime du scope
 * documentaire (`ctx.reglerLeSuivi`), la source que le bouton affiche. C'est ce
 * qui garantit qu'après l'appel de l'agent, le bouton dit le nouvel état.
 *
 * Le reçu dit le mode RELU sur l'éditeur après l'appel, jamais le mode demandé :
 * un reçu qui répète la demande ne prouve rien.
 */

import type { CanvasActionInput, CanvasDocumentMode } from '@hector/shared-types';
import { modeDuRegime } from '../modeDuDocument';
import { abortedBeforeMutation, buildVerifiedReceipt, toCanvasSnapshotSummary, type CanvasActionReceipt } from './actionReceipt';
import { idsDeRevision } from './canvasSnapshot';
import { snapshot, type CanvasActionContext } from './textActionExecutors';

/** Le régime que dit un mode d'éditeur, ou `null` quand le mode n'en dit aucun (`viewing`, `unknown`). */
function regimeQueDitLeMode(mode: CanvasDocumentMode): boolean | null {
  return mode === modeDuRegime(true) ? true : mode === modeDuRegime(false) ? false : null;
}

function enClair(suiviActif: boolean): 'on' | 'off' {
  return suiviActif ? 'on' : 'off';
}

/**
 * Règle le régime de suivi du document sur `input.enabled`, puis rend un reçu
 * qui dit ce que l'éditeur porte RÉELLEMENT après l'appel.
 *
 * Trois règles, et chacune ferme un mensonge possible :
 *
 * - **Tout ce qui peut refuser passe AVANT le réglage.** La lecture du document
 *   et la vérification d'époque précèdent l'écriture du régime ; après elle, il
 *   n'y a plus aucune attente. Un refus rendu après coup annoncerait un échec
 *   alors que le bouton a déjà changé.
 * - **L'instantané d'après est celui d'avant** : régler le suivi n'écrit rien
 *   dans l'acte, et aucune attente ne sépare la lecture du réglage — rien n'a pu
 *   s'intercaler.
 * - **Si l'éditeur ne prend pas le mode demandé, le régime est ramené à ce que
 *   l'éditeur fait.** Le bouton et les frappes ne restent jamais en désaccord
 *   (c'est le défaut que #1838 corrige), et le reçu `failed` nomme l'état réel.
 */
export async function executeSetReviewMode(
  ctx: CanvasActionContext,
  input: Extract<CanvasActionInput, { action: 'set_review_mode' }>,
): Promise<CanvasActionReceipt> {
  const pre = await snapshot(ctx);
  if (ctx.reglerLeSuivi === undefined) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'CAPABILITY_UNAVAILABLE',
      message: 'set_review_mode cannot reach the change-tracking setting of this document: no canvas document is '
        + 'mounted for this conversation. Nothing was changed. Run canvas_inspect to read the document mounted now, '
        + 'then resend the action.',
    });
  }
  // La lecture ci-dessus est une attente : l'avocat a pu ouvrir un autre
  // document. Dernière vérification possible — après elle, tout est synchrone.
  ctx.verifierEpoque('set_review_mode:avant-reglage');

  const regimeAvant = ctx.regimeSuivi !== false;
  let modeRelu = ctx.reglerLeSuivi(input.enabled);
  const conforme = modeRelu === modeDuRegime(input.enabled);
  // L'éditeur n'a pas pris le mode demandé : le régime suit ce que l'éditeur
  // fait. Un mode qui ne dit aucun régime (`viewing`, `unknown`) rend le régime
  // d'avant l'appel.
  const regimeReel = conforme ? input.enabled : regimeQueDitLeMode(modeRelu) ?? regimeAvant;
  if (!conforme) modeRelu = ctx.reglerLeSuivi(regimeReel);

  const etatReel = `Change tracking is ${enClair(regimeReel)} (toolbar button), and the editor is in "${modeRelu}" mode.`;
  const instantane = toCanvasSnapshotSummary(pre);
  const revisions = idsDeRevision(pre.trackedChanges);
  const receipt = buildVerifiedReceipt({
    intent: input.action,
    regimeSuivi: regimeReel,
    preSnapshot: instantane,
    postSnapshot: instantane,
    resolvedTargets: [],
    provider: undefined,
    revisionsAvant: revisions,
    revisionsApres: revisions,
    // Régler le suivi n'écrit rien dans l'acte : aucune révision ne peut naître.
    mouvementAttendu: 'aucun',
    verification: [{
      name: 'review_mode_read_back',
      passed: conforme,
      message: conforme
        ? `Read back after the call. ${etatReel}`
        : `set_review_mode asked for change tracking ${enClair(input.enabled)} (editor mode `
          + `"${modeDuRegime(input.enabled)}"), and the editor did not take it. ${etatReel}`,
    }],
    effects: [{ kind: 'set_review_mode', reviewability: 'none', description: etatReel }],
  });
  if (conforme) return receipt;
  return {
    ...receipt,
    errors: [{
      code: 'REVIEW_MODE_NOT_APPLIED',
      message: `canvas_perform_action set_review_mode enabled:${String(input.enabled)} was NOT applied: the editor did `
        + `not switch to "${modeDuRegime(input.enabled)}" mode. ${etatReel} The document itself is untouched. Do not `
        + 'resend this action: tell the lawyer the setting could not be changed from here, and that the toolbar '
        + 'button "Suivi des modifications" shows the current state.',
    }],
    recovery: {
      action: 'stop',
      reason: 'The change-tracking setting was not applied and was left as the editor reports it; resending would '
        + 'fail the same way.',
    },
  };
}
